/**
 * TanStack Query hooks for nowcast / economic outlook endpoints.
 *
 * Endpoints:
 *   GET /api/economic-outlook/countries                              -> runtime country config
 *   GET /api/economic-outlook/nowcasts?country&timeframe             -> nowcast series
 *   GET /api/economic-outlook/nowcasts/delta/<date>?country          -> week-over-week delta
 *   GET /api/economic-outlook/nowcasts/daily-deltas/<date>           -> per-day delta breakdown
 *   GET /api/economic-outlook/releases/upcoming                      -> workbook-backed upcoming strip
 *   GET /api/economic-outlook/releases/week/<date>                   -> full week (release rows)
 *   GET /api/economic-outlook/inflation?country&type&timeframe       -> inflation fan chart
 *   GET /api/economic-outlook/recession?country&timeframe            -> recession + recovery probs
 *   GET /api/economic-outlook/releases/indicator/<name>/chart        -> indicator forecast chart
 *   GET /api/economic-outlook/releases/indicator/<name>/metadata     -> indicator schema metadata
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiGet } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';
import type {
  CountriesConfigResponse,
  CountryIso,
  IndicatorChartResponse,
  IndicatorMetadataResponse,
  InflationDataResponse,
  InflationType,
  NowcastDailyDeltasResponse,
  NowcastDataResponse,
  NowcastDeltaResponse,
  RecessionDataResponse,
  Timeframe,
} from './types';

interface ReleasesResponse {
  success: boolean;
  /** Raw upcoming-release rows; `mapApiRelease` normalizes them in the panel. */
  releases: ApiReleaseItem[];
  count?: number;
}

interface ApiReleaseDay {
  date: string;
  date_formatted?: string;
  date_formatted_full?: string;
  releases: ApiReleaseItem[];
}

/**
 * Raw release row from /api/economic-outlook/releases ({week/<date>,upcoming}). The backend
 * always sends `time: ""` and the real date in `date` (YYYY-MM-DD).
 */
interface ApiReleaseItem {
  release: string;
  actual?: string | number | null;
  previous?: string | number | null;
  fulcrum?: string | number | null;
  consensus?: string | null;
  time?: string;
  date?: string;
  [key: string]: unknown;
}

interface WeekReleasesResponse {
  success: boolean;
  releases: ApiReleaseItem[];
  daily_releases?: ApiReleaseDay[];
  week_start?: string;
  week_title?: string;
  count?: number;
}

export function useCountries(): UseQueryResult<CountriesConfigResponse> {
  return useQuery<CountriesConfigResponse>({
    queryKey: ['nowcasts', 'countries'],
    queryFn: () =>
      apiGet<CountriesConfigResponse>(endpoints.config.economicOutlookCountries()),
  });
}

export function useNowcasts(
  country: CountryIso | null,
  timeframe: Timeframe,
): UseQueryResult<NowcastDataResponse> {
  return useQuery<NowcastDataResponse>({
    queryKey: ['nowcasts', 'data', country, timeframe],
    queryFn: () =>
      apiGet<NowcastDataResponse>(
        endpoints.forecasts.nowcast({ country: country ?? '', timeframe }),
      ),
    enabled: Boolean(country),
  });
}

export function useNowcastDelta(
  date: string | null,
  country: CountryIso,
): UseQueryResult<NowcastDeltaResponse> {
  return useQuery<NowcastDeltaResponse>({
    queryKey: ['nowcasts', 'delta', date, country],
    queryFn: () =>
      apiGet<NowcastDeltaResponse>(
        endpoints.releases.nowcastDelta(date ?? '', { country }),
      ),
    enabled: Boolean(date),
  });
}

export function useDailyDeltas(
  date: string | null,
  country: CountryIso,
): UseQueryResult<NowcastDailyDeltasResponse> {
  return useQuery<NowcastDailyDeltasResponse>({
    queryKey: ['nowcasts', 'daily-deltas', date, country],
    queryFn: () =>
      apiGet<NowcastDailyDeltasResponse>(
        endpoints.releases.nowcastDailyDeltas(date ?? '', { country }),
      ),
    enabled: Boolean(date),
  });
}

export function useWeekReleases(
  date: string | null,
  country: CountryIso,
): UseQueryResult<WeekReleasesResponse> {
  return useQuery<WeekReleasesResponse>({
    queryKey: ['nowcasts', 'week-releases', date, country],
    queryFn: () =>
      apiGet<WeekReleasesResponse>(
        endpoints.releases.nowcastWeek(date ?? '', { country }),
      ),
    enabled: Boolean(date),
  });
}

export function useUpcomingReleases(country: CountryIso): UseQueryResult<ReleasesResponse> {
  // Workbook-backed source (one row per indicator with a Fulcrum forecast
  // in S3) so clicking a row always lands on a workbook the chart-modal
  // endpoint can read. US-only — the workbook collection lives under
  // Releases/US/; non-US countries skip the fetch entirely to match
  // prod legacy's `upcoming_releases_enabled` country gate.
  return useQuery<ReleasesResponse>({
    queryKey: ['nowcasts', 'upcoming-releases', country],
    queryFn: () =>
      apiGet<ReleasesResponse>(endpoints.releases.upcoming({ limit: 25 })),
    enabled: country === 'US',
  });
}

/**
 * /api/economic-outlook/inflation — inflation fan chart for the Inflation view.
 *
 * The mars-forecasts feature has a similar wrapper used by the dashboard
 * Forecasts mini; this hook is a feature-local copy so the nowcasts module
 * stays self-contained per the migration's "one feature folder per page"
 * convention.
 */
export function useInflationData(
  country: CountryIso | null,
  inflationType: InflationType,
  timeframe: Timeframe,
): UseQueryResult<InflationDataResponse> {
  return useQuery<InflationDataResponse>({
    queryKey: ['nowcasts', 'inflation', country, inflationType, timeframe],
    queryFn: () =>
      apiGet<InflationDataResponse>(
        endpoints.forecasts.inflation({
          country: country ?? '',
          type: inflationType,
          timeframe,
        }),
      ),
    enabled: Boolean(country),
  });
}

/**
 * /api/economic-outlook/recession — daily recession + strong-recovery
 * probabilities. Powers the GDP-view "Recession" selector option.
 */
export function useRecessionProbabilities(
  country: CountryIso | null,
  timeframe: Timeframe,
): UseQueryResult<RecessionDataResponse> {
  return useQuery<RecessionDataResponse>({
    queryKey: ['nowcasts', 'recession', country, timeframe],
    queryFn: () =>
      apiGet<RecessionDataResponse>(
        endpoints.forecasts.recession({ country: country ?? '', timeframe }),
      ),
    enabled: Boolean(country),
  });
}

/**
 * /api/economic-outlook/releases/indicator/<indicator_name>/chart — split-panel
 * forecast-tracking chart for one Upcoming-Release indicator. Fires only
 * when `indicator` is set (i.e. the modal is open).
 */
export function useIndicatorChart(
  indicator: string | null,
): UseQueryResult<IndicatorChartResponse> {
  return useQuery<IndicatorChartResponse>({
    queryKey: ['releases-tracker', 'indicator-chart', indicator],
    queryFn: () =>
      apiGet<IndicatorChartResponse>(
        endpoints.releases.indicatorChart(indicator ?? ''),
      ),
    enabled: Boolean(indicator),
  });
}

/**
 * /api/economic-outlook/releases/indicator/<indicator_name>/metadata — schema metadata for
 * one indicator. The modal reads `display_units` for the chart's y-axis
 * label; a 404 is non-fatal (the modal falls back to "Level").
 */
export function useIndicatorMetadata(
  indicator: string | null,
): UseQueryResult<IndicatorMetadataResponse> {
  return useQuery<IndicatorMetadataResponse>({
    queryKey: ['releases-tracker', 'indicator-metadata', indicator],
    queryFn: () =>
      apiGet<IndicatorMetadataResponse>(
        endpoints.releases.indicatorMetadata(indicator ?? ''),
      ),
    enabled: Boolean(indicator),
    // Metadata is optional decoration — a missing schema row 404s; don't
    // retry and don't surface the error as a modal failure.
    retry: false,
  });
}
